/**
 * Scouting React Query Hooks
 * Wraps scoutingService functions with TanStack Query (v5).
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  getFollowedPlayers,
  followPlayer,
  unfollowPlayer,
  updatePlayerNotes,
  getWatchedMatches,
  watchMatch,
  unwatchMatch,
  getScoutingAgenda,
  getDiscoveredMatches,
  getAllMatchNotes,
  upsertMatchNote,
  deleteMatchNote,
} from "../../services/scoutingService";

// ─── Query Keys ──────────────────────────────────────────────────────────────

export const scoutingKeys = {
  all: ["scouting"],
  players: (userId) => ["scouting", "players", userId],
  matches: (userId) => ["scouting", "matches", userId],
  agenda: (userId) => ["scouting", "agenda", userId],
  discoveredMatches: (userId) => ["scouting", "discovered", userId],
  matchNotes: (userId) => ["scouting", "matchNotes", userId],
};

// ─── Queries ─────────────────────────────────────────────────────────────────

/**
 * Fetch all followed players for a user.
 */
export function useFollowedPlayers(userId) {
  const navigate = useNavigate();
  return useQuery({
    queryKey: scoutingKeys.players(userId),
    queryFn: () => getFollowedPlayers(navigate),
    enabled: !!userId,
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * Fetch all watched matches for a user.
 */
export function useWatchedMatches(userId) {
  const navigate = useNavigate();
  return useQuery({
    queryKey: scoutingKeys.matches(userId),
    queryFn: () => getWatchedMatches(navigate),
    enabled: !!userId,
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * Fetch upcoming scouting agenda for a user.
 */
export function useScoutingAgenda(userId) {
  const navigate = useNavigate();
  return useQuery({
    queryKey: scoutingKeys.agenda(userId),
    queryFn: () => getScoutingAgenda(navigate),
    enabled: !!userId,
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * Fetch auto-discovered matches for followed players' teams (current season).
 */
export function useDiscoveredMatches(userId) {
  const navigate = useNavigate();
  return useQuery({
    queryKey: scoutingKeys.discoveredMatches(userId),
    queryFn: () => getDiscoveredMatches(navigate),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });
}

// ─── Mutations ────────────────────────────────────────────────────────────────

/**
 * Follow a player with optimistic update.
 */
export function useFollowPlayer() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: ({ userId, jugadorId, playerData }) =>
      followPlayer(jugadorId, playerData, navigate),
    onMutate: async ({ userId, jugadorId, playerData }) => {
      await queryClient.cancelQueries({ queryKey: scoutingKeys.players(userId) });
      const previous = queryClient.getQueryData(scoutingKeys.players(userId));

      queryClient.setQueryData(scoutingKeys.players(userId), (old = []) => {
        const exists = old.find((p) => String(p.jugadorId) === String(jugadorId));
        if (exists) return old;
        return [
          ...old,
          {
            jugadorId,
            ...playerData,
            followedAt: new Date().toISOString(),
            _optimistic: true,
          },
        ];
      });

      return { previous };
    },
    onError: (_err, { userId }, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(scoutingKeys.players(userId), context.previous);
      }
    },
    onSettled: (_data, _err, { userId }) => {
      queryClient.invalidateQueries({ queryKey: scoutingKeys.players(userId), refetchType: 'none' });
      queryClient.invalidateQueries({ queryKey: scoutingKeys.discoveredMatches(userId), refetchType: 'none' });
    },
  });
}

/**
 * Unfollow a player with optimistic update.
 */
export function useUnfollowPlayer() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: ({ userId, jugadorId }) =>
      unfollowPlayer(jugadorId, navigate),
    onMutate: async ({ userId, jugadorId }) => {
      await queryClient.cancelQueries({ queryKey: scoutingKeys.players(userId) });
      const previous = queryClient.getQueryData(scoutingKeys.players(userId));

      queryClient.setQueryData(scoutingKeys.players(userId), (old = []) =>
        old.filter((p) => String(p.jugadorId) !== String(jugadorId))
      );

      return { previous };
    },
    onError: (_err, { userId }, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(scoutingKeys.players(userId), context.previous);
      }
    },
    onSettled: (_data, _err, { userId }) => {
      queryClient.invalidateQueries({ queryKey: scoutingKeys.players(userId), refetchType: 'none' });
      queryClient.invalidateQueries({ queryKey: scoutingKeys.discoveredMatches(userId), refetchType: 'none' });
    },
  });
}

/**
 * Update notes for a followed player.
 */
export function useUpdatePlayerNotes() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: ({ userId, jugadorId, notes, posiciones, pieDominante }) =>
      updatePlayerNotes(jugadorId, { notes, posiciones, pieDominante }, navigate),
    onSuccess: (_data, { userId, jugadorId, notes, posiciones, pieDominante }) => {
      queryClient.setQueryData(scoutingKeys.players(userId), (old = []) =>
        old.map((p) =>
          String(p.jugadorId) === String(jugadorId)
            ? { ...p, notes, posiciones, pieDominante, updatedAt: new Date().toISOString() }
            : p
        )
      );
    },
    onSettled: (_data, _err, { userId }) => {
      queryClient.invalidateQueries({ queryKey: scoutingKeys.players(userId) });
    },
  });
}

/**
 * Mark a match to watch with optimistic update.
 */
export function useWatchMatch() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: ({ userId, partidoId, matchData }) =>
      watchMatch(partidoId, matchData, navigate),
    onMutate: async ({ userId, partidoId, matchData }) => {
      await queryClient.cancelQueries({ queryKey: scoutingKeys.matches(userId) });
      await queryClient.cancelQueries({ queryKey: scoutingKeys.agenda(userId) });
      const previousMatches = queryClient.getQueryData(scoutingKeys.matches(userId));
      const previousAgenda = queryClient.getQueryData(scoutingKeys.agenda(userId));

      queryClient.setQueryData(scoutingKeys.matches(userId), (old = []) => {
        const exists = old.find((m) => String(m.partidoId) === String(partidoId));
        if (exists) return old;
        return [
          ...old,
          { partidoId, ...matchData, markedAt: new Date().toISOString(), _optimistic: true },
        ];
      });

      // Also optimistically add to agenda so "My Schedule" shows it immediately
      const matchFecha = matchData.fecha ? new Date(matchData.fecha) : null;
      if (!matchFecha || matchFecha > new Date()) {
        queryClient.setQueryData(scoutingKeys.agenda(userId), (old = []) => {
          const exists = old.find((m) => String(m.partidoId) === String(partidoId));
          if (exists) return old;
          return [
            ...old,
            { partidoId, ...matchData, markedAt: new Date().toISOString(), _optimistic: true },
          ];
        });
      }

      return { previousMatches, previousAgenda };
    },
    onError: (_err, { userId }, context) => {
      if (context?.previousMatches !== undefined) {
        queryClient.setQueryData(scoutingKeys.matches(userId), context.previousMatches);
      }
      if (context?.previousAgenda !== undefined) {
        queryClient.setQueryData(scoutingKeys.agenda(userId), context.previousAgenda);
      }
    },
    onSuccess: (_data, { userId, partidoId }) => {
      // Confirm the optimistic entry (remove _optimistic flag) so the button stays yellow
      queryClient.setQueryData(scoutingKeys.matches(userId), (old = []) =>
        old.map((m) =>
          String(m.partidoId) === String(partidoId) && m._optimistic
            ? { ...m, _optimistic: undefined }
            : m
        )
      );
    },
    onSettled: (_data, _err, { userId }) => {
      // Mark as stale but don't immediately refetch matches — prevents server-race reverting the UI
      queryClient.invalidateQueries({ queryKey: scoutingKeys.matches(userId), refetchType: 'none' });
      queryClient.invalidateQueries({ queryKey: scoutingKeys.agenda(userId) });
    },
  });
}

/**
 * Unmark a match with optimistic update.
 */
export function useUnwatchMatch() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: ({ userId, partidoId }) =>
      unwatchMatch(partidoId, navigate),
    onMutate: async ({ userId, partidoId }) => {
      await queryClient.cancelQueries({ queryKey: scoutingKeys.matches(userId) });
      await queryClient.cancelQueries({ queryKey: scoutingKeys.agenda(userId) });
      const previousMatches = queryClient.getQueryData(scoutingKeys.matches(userId));
      const previousAgenda = queryClient.getQueryData(scoutingKeys.agenda(userId));

      queryClient.setQueryData(scoutingKeys.matches(userId), (old = []) =>
        old.filter((m) => String(m.partidoId) !== String(partidoId))
      );

      queryClient.setQueryData(scoutingKeys.agenda(userId), (old = []) =>
        old.filter((m) => String(m.partidoId) !== String(partidoId))
      );

      return { previousMatches, previousAgenda };
    },
    onError: (_err, { userId }, context) => {
      if (context?.previousMatches !== undefined) {
        queryClient.setQueryData(scoutingKeys.matches(userId), context.previousMatches);
      }
      if (context?.previousAgenda !== undefined) {
        queryClient.setQueryData(scoutingKeys.agenda(userId), context.previousAgenda);
      }
    },
    onSettled: (_data, _err, { userId }) => {
      // Mark as stale but don't immediately refetch — prevents server-race reverting the UI
      queryClient.invalidateQueries({ queryKey: scoutingKeys.matches(userId), refetchType: 'none' });
      queryClient.invalidateQueries({ queryKey: scoutingKeys.agenda(userId), refetchType: 'none' });
    },
  });
}

// ─── Match Notes ──────────────────────────────────────────────────────────────

/**
 * Fetch all match notes for a user.
 */
export function useMatchNotes(userId) {
  const navigate = useNavigate();
  return useQuery({
    queryKey: scoutingKeys.matchNotes(userId),
    queryFn: () => getAllMatchNotes(navigate),
    enabled: !!userId,
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * Upsert a match note (jugador + partido).
 */
export function useUpsertMatchNote() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: ({ userId, jugadorId, partidoId, noteData }) =>
      upsertMatchNote(jugadorId, partidoId, noteData, navigate),
    onSuccess: (_data, { userId }) => {
      queryClient.invalidateQueries({ queryKey: scoutingKeys.matchNotes(userId) });
    },
  });
}

/**
 * Delete a match note.
 */
export function useDeleteMatchNote() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: ({ userId, jugadorId, partidoId }) =>
      deleteMatchNote(jugadorId, partidoId, navigate),
    onSuccess: (_data, { userId }) => {
      queryClient.invalidateQueries({ queryKey: scoutingKeys.matchNotes(userId) });
    },
  });
}
